import {useCallback, useEffect, useMemo, useState} from "react";
import {createPortal} from "react-dom";
import {Link} from "react-router-dom";
import {getCart, removeCartItem, updateCartItem, dispatchHeaderSearchClose, formatMoneyDisplay, productImageFromUrl, type CartDetailsResponse, type CartLine} from "@corporatesclub/shared";
import {DsButton, DsEmptyState} from "@corporatesclub/ui";
import {paths} from "@/lib/paths";
import type {CartAddedDetail} from "@/hooks/useAddToCart";
import {useI18n} from "./I18nProvider";
import {useCartDrawer} from "./CartDrawerProvider";
import {useShopSite} from "@/components/shop/ShopSiteProvider";
import {useSession} from "./SessionProvider";
import {MoneyDisplay} from "./MoneyDisplay";
import {ProductPrice} from "./ProductPrice";

function cartLineKey(line: Pick<CartLine, "warehouse" | "productId">) {
  return `${line.warehouse}-${line.productId}`;
}

function sortCartLinesPinned(lines: CartLine[], pinnedKey: string | null): CartLine[] {
  if(!pinnedKey) return lines;
  const idx = lines.findIndex((l) => cartLineKey(l) === pinnedKey);
  if(idx <= 0) return lines;
  const next = [...lines];
  const [item] = next.splice(idx, 1);
  next.unshift(item);
  return next;
}

function DrawerLine({
  line,
  onQty,
  onRemove,
  onNavigate,
  busy,
  t
}: {
  line: CartLine;
  onQty: (warehouse: string, productId: number, qty: number) => void;
  onRemove: (warehouse: string, productId: number) => void;
  onNavigate: () => void;
  busy: boolean;
  t: (k: import("@corporatesclub/shared").I18nKey) => string;
}) {
  const [draftQty, setDraftQty] = useState(String(line.quantity));

  useEffect(() => {
    setDraftQty(String(line.quantity));
  }, [line.quantity]);

  function onDraftChange(raw: string) {
    const digits = raw.replace(/\D/g, "").slice(0, 3);
    setDraftQty(digits);
  }

  function commitDraft() {
    const parsed = parseInt(draftQty, 10);
    if(!draftQty || Number.isNaN(parsed) || parsed < 1) {
      setDraftQty(String(line.quantity));
      return;
    }
    const next = Math.min(999, parsed);
    setDraftQty(String(next));
    if(next !== line.quantity) onQty(line.warehouse, line.productId, next);
  }

  return (
    <article className="cc-cartDrawer__item">
      <Link to={line.product.url} className="cc-cartDrawer__thumb anim" onClick={onNavigate}>
        <img src={productImageFromUrl(line.product.imagine)} alt={line.product.nume} />
      </Link>
      <div className="cc-cartDrawer__itemTop">
        <div className="cc-cartDrawer__itemCopy">
          {line.product.brand ? <span className="cc-cartDrawer__brand">{line.product.brand}</span> : null}
          <Link to={line.product.url} className="cc-cartDrawer__name anim" onClick={onNavigate}>{line.product.nume}</Link>
        </div>
        <div className="cc-cartDrawer__linePrice">
          <ProductPrice pretEuro={line.pretEuro || line.product.pretEuro} pretLei={line.pretLei} />
        </div>
      </div>
      <div className="cc-cartDrawer__controls">
        <button type="button" className="cc-cartDrawer__qtyBtn" aria-label="-" disabled={busy} onClick={() => onQty(line.warehouse, line.productId, line.quantity - 1)}>
          <i className="fa fa-minus" aria-hidden="true" />
        </button>
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={3}
          className="cc-cartDrawer__qtyInput"
          value={draftQty}
          disabled={busy}
          aria-label={t("Qty")}
          onChange={(e) => onDraftChange(e.target.value)}
          onBlur={commitDraft}
          onKeyDown={(e) => { if(e.key === "Enter") { e.preventDefault(); (e.target as HTMLInputElement).blur(); } }}
        />
        <button type="button" className="cc-cartDrawer__qtyBtn cc-cartDrawer__qtyBtn--plus" aria-label="+" disabled={busy} onClick={() => onQty(line.warehouse, line.productId, line.quantity + 1)}>
          <i className="fa fa-plus" aria-hidden="true" />
        </button>
        <button type="button" className="cc-cartDrawer__removeBtn" aria-label={t("Remove")} disabled={busy} onClick={() => onRemove(line.warehouse, line.productId)}>
          <i className="fa fa-trash-o" aria-hidden="true" />
        </button>
      </div>
    </article>
  );
}

export function CartDrawer() {
  const {t, currency} = useI18n();
  const {user} = useSession();
  const {status, ready: siteReady} = useShopSite();
  const canUseCart = user?.authenticated || (siteReady && status.guestOrdersAllowed);
  const {isOpen, open, close} = useCartDrawer();
  const leaveForPage = useCallback(() => {
    close();
    dispatchHeaderSearchClose();
  }, [close]);
  const [cart, setCart] = useState<CartDetailsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [pinnedKey, setPinnedKey] = useState<string | null>(null);

  const load = useCallback(() => {
    if(!canUseCart) {
      setCart(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    getCart()
      .then(setCart)
      .catch(() => setCart(null))
      .finally(() => setLoading(false));
  }, [canUseCart]);

  useEffect(() => { setMounted(true); }, []);

  useEffect(() => {
    function onAdded(e: Event) {
      const d = (e as CustomEvent<CartAddedDetail>).detail;
      if(d.addedLine) setPinnedKey(cartLineKey(d.addedLine));
      open();
      load();
    }
    window.addEventListener("cart-added", onAdded);
    return () => window.removeEventListener("cart-added", onAdded);
  }, [open, load]);

  useEffect(() => {
    if(!isOpen) {
      setPinnedKey(null);
      return;
    }
    load();
    document.body.classList.add("cc-cartDrawer-open");
    function onKey(e: KeyboardEvent) {
      if(e.key === "Escape") close();
    }
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.classList.remove("cc-cartDrawer-open");
      window.removeEventListener("keydown", onKey);
    };
  }, [isOpen, load, close]);

  async function onRemove(warehouse: string, productId: number) {
    setBusy(true);
    try {
      const r = await removeCartItem(warehouse, productId);
      setCart(r);
      window.dispatchEvent(new CustomEvent("cart-updated", {detail: r.count}));
    } finally {
      setBusy(false);
    }
  }

  async function onQty(warehouse: string, productId: number, qty: number) {
    const next = Math.max(1, qty);
    setBusy(true);
    try {
      const r = await updateCartItem(warehouse, productId, next);
      setCart(r);
      window.dispatchEvent(new CustomEvent("cart-updated", {detail: r.count}));
    } finally {
      setBusy(false);
    }
  }

  const displayLines = useMemo(
    () => (cart?.lines ? sortCartLinesPinned(cart.lines, pinnedKey) : []),
    [cart?.lines, pinnedKey]
  );

  if(!mounted) return null;

  const rate = cart?.eurRate || 4.97;
  const hasDiscount = cart ? cart.discount > 0 : false;
  const displaySub = cart ? (hasDiscount ? cart.totalAfterDiscount : cart.subtotal) : 0;
  const total = cart && cart.shippingCost === null ? displaySub : (cart?.grandTotal ?? 0);
  const shipRemain = cart ? formatMoneyDisplay(cart.shippingRemainingLei, currency, rate) : "";
  const shipProgress = cart ? Math.min(100, Math.round((cart.subtotal / 100) * 100)) : 0;

  const panel = (
    <div className={`cc-cartDrawer${isOpen ? " cc-cartDrawer--open" : ""}`} aria-hidden={!isOpen}>
      <button type="button" className="cc-cartDrawer__backdrop" aria-label={t("Close")} onClick={close} tabIndex={isOpen ? 0 : -1} />
      <aside className="cc-cartDrawer__panel" role="dialog" aria-modal="true" aria-label={t("My cart")}>
        <header className="cc-cartDrawer__head">
          <h2 className="cc-cartDrawer__title">{t("My cart")}</h2>
          <button type="button" className="cc-cartDrawer__close" onClick={close} aria-label={t("Close")}>
            <span className="material-symbols-rounded" aria-hidden="true">close</span>
          </button>
        </header>

        <div className="cc-cartDrawer__body">
          {loading ? (
            <div className="cc-cartDrawer__loading" aria-busy="true">
              <div className="cc-cartDrawer__spinner" />
              <span>{t("Loading products...")}</span>
            </div>
          ) : !displayLines.length ? (
            <div className="cc-cartDrawer__empty">
              <DsEmptyState icon={<i className="fa fa-shopping-bag" aria-hidden="true" />} title={t("Your cart is empty")} />
              {!user?.authenticated && !(siteReady && status.guestOrdersAllowed) ? (
                <p className="cc-cartDrawer__guestHint">
                  <Link to={paths.login} onClick={leaveForPage}>{t("Sign in to add items to your cart.")}</Link>
                </p>
              ) : null}
            </div>
          ) : (
            <div className="cc-cartDrawer__items">
              {displayLines.map((line) => (
                <DrawerLine key={`${line.warehouse}-${line.productId}`} line={line} onQty={onQty} onRemove={onRemove} onNavigate={leaveForPage} busy={busy} t={t} />
              ))}
            </div>
          )}
        </div>

        {cart && displayLines.length ? (
          <footer className="cc-cartDrawer__foot">
            {cart.freeDelivery ? (
              <p className="cc-cartDrawer__shipBanner">🎉 {t("Congratulations, free shipping!")}</p>
            ) : (
              <div className="cc-cartDrawer__shipHint">
                <span>{t("Add another")} {shipRemain} {t("to your shopping bag to get FREE DELIVERY!")}</span>
                <div
                  className="cc-cartDrawer__shipBar"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={shipProgress}
                  aria-label={`${shipProgress}% ${t("Free shipping title")}`}
                >
                  <div className="cc-cartDrawer__shipBarFill" style={{width: `${shipProgress}%`}} />
                </div>
              </div>
            )}
            <div className="cc-cartDrawer__totalRow">
              <span className="cc-cartDrawer__totalLabel">{t("Total (VAT incl.)")}</span>
              <span className="cc-cartDrawer__totalVal"><MoneyDisplay lei={total} eurRate={rate} /></span>
            </div>
            <div className="cc-cartDrawer__trust">
              <span className="cc-cartDrawer__trustItem">
                <span className="material-symbols-rounded" aria-hidden="true">verified_user</span>
                {t("Secure payment")}
              </span>
              <span className="cc-cartDrawer__trustItem">
                <span className="material-symbols-rounded" aria-hidden="true">bolt</span>
                {t("Quick checkout")}
              </span>
            </div>
            <Link to={cart.checkoutUrl || paths.checkout} className="cc-cartDrawer__checkout anim" onClick={leaveForPage}>
              <DsButton variant="primary" size="md" fullWidth>
                {t("Checkout")} <i className="fa fa-long-arrow-right" aria-hidden="true" />
              </DsButton>
            </Link>
            <Link to={paths.cart} className="cc-cartDrawer__cartLink anim" onClick={leaveForPage}>
              {t("Go to cart")}
            </Link>
          </footer>
        ) : null}
      </aside>
    </div>
  );

  return createPortal(panel, document.body);
}
